import React, { useState } from 'react';
import {
  Container,
  Card,
  Button,
  Form,
  Row,
  Col,
  Spinner,
} from 'react-bootstrap';
import Select from 'react-select';
import { FaSearch, FaSpinner, FaUserPlus, FaCalendarAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import MaskedInput from 'react-text-mask';
import Header from '~/Components/Header/Header';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { mask, format } from '~/Utils/Helpers';
import {
  buscarCheckinEventoRsvp,
  confirmarPendenteCheckinEventoRsvp,
  registrarProspectoCheckinEventoRsvp,
  vincularEmpreendimento,
} from '~/Services/Service';
import ModalAdicionarProspectoRapido from '~/Pages/LancamentoCadastroRapido/ModalAdicionarProspectoRapido';
import CardConfirmado from './components/CardConfirmado';
import CardPendente from './components/CardPendente';
import CardProspecto from './components/CardProspecto';

const CODIGO_FAMILIA_SETGARDEN = 11384;
const CODIGO_CORRETOR = 1078;
const CODIGO_GERENTE = 1077;

// Cada tipo declara label, mask (false = sem máscara) e input ('text' | 'tel').
// Inputs 'tel' têm o filtro normalizado via format.clear antes de ir pro backend
// (mesmo padrão do /prospecto/buscar).
const searchType = {
  cpf: {
    label: 'CPF',
    mask: mask.cpf_cnpj,
    input: 'tel',
  },
  nome: {
    label: 'Nome',
    mask: false,
    input: 'text',
  },
  telefone: {
    label: 'Telefone',
    mask: mask.celular,
    input: 'tel',
  },
  prospecto: {
    label: 'Prospecto',
    mask: false,
    input: 'tel',
  },
};

const tipoOptions = Object.keys(searchType).map((key) => ({
  value: key,
  label: searchType[key].label,
}));

const touchStyle = {
  minHeight: 44,
  fontSize: '1rem',
};

const soDigitos = (v) => String(v || '').replace(/\D/g, '');

const extrairMensagemErro = (err, fallback) => {
  if (err && err.message) return err.message;
  if (typeof err === 'string') return err;
  return fallback;
};

const EventoRsvpCheckin = () => {
  const [tipo, setTipo] = useState('cpf');
  const [filtro, setFiltro] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [evento, setEvento] = useState(null);
  const [confirmados, setConfirmados] = useState(null);
  const [pendentes, setPendentes] = useState([]);
  const [prospectos, setProspectos] = useState([]);
  const [showCadastroLead, setShowCadastroLead] = useState(false);
  // Ações que recarregam a lista (confirmar pendente, vincular prospecto, lead).
  // Guardamos a chave do item em processamento para o spinner local + bloqueio global.
  const [confirmandoCpf, setConfirmandoCpf] = useState(null);
  const [vinculandoProspecto, setVinculandoProspecto] = useState(null);
  const [registrandoLead, setRegistrandoLead] = useState(false);

  // Enquanto a lista estiver carregando (busca ou qualquer ação que a altera),
  // o seletor e o filtro ficam bloqueados (caso 5).
  const carregando =
    buscando ||
    confirmandoCpf !== null ||
    vinculandoProspecto !== null ||
    registrandoLead;

  const buscar = async (e) => {
    if (e) e.preventDefault();

    let valor = filtro.trim();
    // Normaliza igual ao ProspectoBuscaFormulario: tudo que é input='tel'
    // vira só dígitos antes de ir pro backend.
    if (searchType[tipo].input === 'tel') {
      valor = format.clear(valor);
    }

    if (!valor) {
      toast.warn('Informe o filtro de busca.');
      return;
    }

    setBuscando(true);
    setConfirmados(null);
    setPendentes([]);
    setProspectos([]);
    try {
      const response = await buscarCheckinEventoRsvp({
        tipo,
        filtro: valor,
      });
      setEvento(
        response && response.codigo_evento
          ? { codigo: response.codigo_evento, nome: response.nome_evento }
          : null
      );
      const lista = response && response.confirmados ? response.confirmados : [];
      const pend = response && response.pendentes ? response.pendentes : [];
      const prosp = response && response.prospectos ? response.prospectos : [];
      setConfirmados(lista);
      setPendentes(pend);
      setProspectos(prosp);
      if (lista.length === 0 && pend.length === 0 && prosp.length === 0) {
        toast.info('Nenhum resultado para este filtro.');
      }
    } catch (err) {
      toast.error(extrairMensagemErro(err, 'Erro ao buscar.'));
      setConfirmados([]);
    } finally {
      setBuscando(false);
    }
  };

  const atualizarConfirmado = (atualizado) => {
    setConfirmados((lista) =>
      (lista || []).map((c) =>
        c.codigo_confirmacao === atualizado.codigo_confirmacao ? atualizado : c
      )
    );
  };

  // Adiciona/atualiza um confirmado no topo da lista (sem duplicar).
  const prependConfirmado = (novo) => {
    setConfirmados((lista) => {
      const base = (lista || []).filter(
        (c) => c.codigo_confirmacao !== novo.codigo_confirmacao
      );
      return [novo, ...base];
    });
  };

  // Caso 1 — pendente vira confirmado e abre o painel de presença.
  const confirmarPendente = async (cpf) => {
    setConfirmandoCpf(cpf);
    try {
      const confirmado = await confirmarPendenteCheckinEventoRsvp({ cpf });
      prependConfirmado(confirmado);
      setPendentes((lista) =>
        (lista || []).filter((p) => soDigitos(p.cpf) !== soDigitos(cpf))
      );
      toast.success('Presença confirmada. Marque quem chegou no painel.');
    } catch (err) {
      toast.error(extrairMensagemErro(err, 'Erro ao confirmar presença.'));
    } finally {
      setConfirmandoCpf(null);
    }
  };

  // Casos 3 e 4 — vincula o prospecto ao empreendimento do evento e dá entrada.
  // Usado tanto pelo card de prospecto (vincular interesse) quanto pela promoção
  // de acompanhante "amigo de outro empreendimento".
  const vincularProspecto = async (codigoProspecto) => {
    setVinculandoProspecto(codigoProspecto);
    try {
      try {
        await vincularEmpreendimento({
          codigo_prospecto: codigoProspecto,
          codigo_familia: CODIGO_FAMILIA_SETGARDEN,
          codigo_corretor: CODIGO_CORRETOR,
          codigo_gerente: CODIGO_GERENTE,
          respeitar_corretor_escolhido: false,
        });
      } catch (errVinculo) {
        // "Vínculo já existente" é esperado quando o prospecto já está na família.
        const msg = extrairMensagemErro(errVinculo, '');
        if (!/já existente|existente/i.test(msg)) {
          throw errVinculo;
        }
      }
      const confirmado = await registrarProspectoCheckinEventoRsvp({
        codigo_prospecto: codigoProspecto,
      });
      prependConfirmado(confirmado);
      setProspectos((lista) =>
        (lista || []).filter((p) => p.codigo_prospecto !== codigoProspecto)
      );
      toast.success('Prospecto vinculado ao evento e presença registrada.');
    } catch (err) {
      toast.error(extrairMensagemErro(err, 'Erro ao vincular prospecto.'));
    } finally {
      setVinculandoProspecto(null);
    }
  };

  // Caso 2 — lead recém-criado (já vinculado pela criação) entra no evento + presença.
  const onLeadCriado = async (codigoProspecto) => {
    setShowCadastroLead(false);
    if (!codigoProspecto) {
      toast.success('Lead criado.');
      return;
    }
    setRegistrandoLead(true);
    try {
      const confirmado = await registrarProspectoCheckinEventoRsvp({
        codigo_prospecto: codigoProspecto,
      });
      // Lead recém-criado: limpa o que sobrou da busca anterior (pendentes,
      // prospectos e outros confirmados) e deixa só o lead na tela.
      setPendentes([]);
      setProspectos([]);
      setConfirmados([confirmado]);
      toast.success('Lead criado, adicionado ao evento e presença registrada.');
    } catch (err) {
      toast.error(
        extrairMensagemErro(err, 'Lead criado, mas falhou ao registrar presença.')
      );
    } finally {
      setRegistrandoLead(false);
    }
  };

  const totaisCard = confirmados && confirmados.length > 0 && (() => {
    const totalEsperado = confirmados.reduce(
      (acc, c) =>
        acc +
        (c.presencas || []).filter((p) => p.confirma_presenca).length +
        (c.acompanhantes || []).length,
      0
    );
    const totalEntrou = confirmados.reduce(
      (acc, c) =>
        acc +
        (c.presencas || []).filter((p) => p.presenca_entrada).length +
        (c.acompanhantes || []).filter((a) => a.presenca_entrada).length,
      0
    );
    return { totalEsperado, totalEntrou };
  })();

  const semResultados =
    !buscando &&
    confirmados &&
    confirmados.length === 0 &&
    pendentes.length === 0 &&
    prospectos.length === 0;

  return (
    <Container fluid className="px-2 px-md-3 py-2">
      <Header title="Check-in de Evento">
        {evento && (
          <span className="text-muted small">
            <FaCalendarAlt className="mr-1" /> {evento.nome}
          </span>
        )}
      </Header>

      <Card className="mb-2">
        <Card.Body className="py-2 px-3">
          <Form onSubmit={buscar}>
            <Row className="align-items-end">
              <Col xs={12} md={3} className="mb-2">
                <Form.Label className="mb-0 small">Buscar por</Form.Label>
                <Select
                  value={tipoOptions.find((o) => o.value === tipo)}
                  onChange={(opt) => {
                    if (opt && opt.value !== tipo) {
                      setTipo(opt.value);
                      setFiltro('');
                    }
                  }}
                  options={tipoOptions}
                  isSearchable={false}
                  isDisabled={carregando}
                  isLoading={carregando}
                  styles={customStyles}
                  components={{ DropdownIndicator }}
                  placeholder="Tipo..."
                />
              </Col>
              <Col xs={12} md={5} className="mb-2">
                <Form.Label className="mb-0 small">Filtro</Form.Label>
                <MaskedInput
                  className="form-control"
                  type={searchType[tipo].input}
                  name="filtro"
                  mask={searchType[tipo].mask}
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  placeholder="Pesquisar por..."
                  disabled={carregando}
                  style={touchStyle}
                  autoFocus
                />
              </Col>
              <Col xs={6} md={2} className="mb-2">
                <Button
                  type="submit"
                  variant="primary"
                  className="w-100"
                  disabled={carregando}
                  style={{ ...touchStyle, fontWeight: 600 }}
                >
                  {buscando ? <FaSpinner className="fa-spin" /> : <FaSearch />} Buscar
                </Button>
              </Col>
              <Col xs={6} md={2} className="mb-2">
                <Button
                  type="button"
                  variant="success"
                  className="w-100"
                  onClick={() => setShowCadastroLead(true)}
                  disabled={carregando}
                  style={{ ...touchStyle, fontWeight: 600 }}
                  title="Cadastrar nova pessoa que chegou sem cadastro prévio"
                >
                  <FaUserPlus className="mr-1" /> Novo lead
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {totaisCard && (
        <div
          className="mb-3 p-2 px-3"
          style={{
            background: '#e7f5ff',
            border: '1px solid #74c0fc',
            borderRadius: 4,
            color: '#1864ab',
          }}
        >
          <strong>{totaisCard.totalEntrou}</strong> de{' '}
          <strong>{totaisCard.totalEsperado}</strong> pessoas com entrada registrada
          (entre os resultados desta busca).
        </div>
      )}

      {buscando && (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Buscando...</p>
        </div>
      )}

      {semResultados && (
        <Card className="mb-3">
          <Card.Body className="text-center py-4">
            <p className="text-muted mb-0">
              Nenhum resultado para este filtro. Use <strong>Novo lead</strong> acima
              se a pessoa não tem cadastro.
            </p>
          </Card.Body>
        </Card>
      )}

      {!buscando &&
        confirmados &&
        confirmados.map((c) => (
          <CardConfirmado
            key={c.codigo_confirmacao}
            confirmado={c}
            onAtualizar={atualizarConfirmado}
            onPromoverProspecto={vincularProspecto}
          />
        ))}

      {!buscando && pendentes.length > 0 && (
        <>
          <h6 className="text-muted mt-3 mb-2">Pendentes (ainda não confirmaram)</h6>
          {pendentes.map((p) => (
            <CardPendente
              key={`pend-${soDigitos(p.cpf)}-${p.tipo}`}
              pendente={p}
              confirmando={confirmandoCpf !== null && soDigitos(confirmandoCpf) === soDigitos(p.cpf)}
              onConfirmar={confirmarPendente}
            />
          ))}
        </>
      )}

      {!buscando && prospectos.length > 0 && (
        <>
          <h6 className="text-muted mt-3 mb-2">
            Prospectos no sistema (vincular ao evento)
          </h6>
          {prospectos.map((p) => (
            <CardProspecto
              key={`prosp-${p.codigo_prospecto}`}
              prospecto={p}
              vinculando={vinculandoProspecto === p.codigo_prospecto}
              onVincular={vincularProspecto}
            />
          ))}
        </>
      )}

      <ModalAdicionarProspectoRapido
        show={showCadastroLead}
        onHide={() => setShowCadastroLead(false)}
        onProspectoCriado={onLeadCriado}
        codigoFamilia={CODIGO_FAMILIA_SETGARDEN}
        codigoCorretor={CODIGO_CORRETOR}
        codigoGerente={CODIGO_GERENTE}
        titulo="Novo Lead - SetGarden"
      />
    </Container>
  );
};

export default EventoRsvpCheckin;
